import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import SharedInputText from '../../components/Shared/SharedInputText';
import SharedDropdown from '../../components/Shared/SharedDropdown';
import { DeleteIcon } from '../../components/Shared/TableActionIcons';
import { getERPClient } from '../../services/supplyChainApi';
import ERPItemSearchField from './ERPItemSearchField';

const createItem = () => ({
  item_id: '',
  sku_id: '',
  item_code: '',
  item_name: '',
  uom_id: '',
  quantity: 1,
  bonus_qty: 0,
  unit_price: 0,
  purchase_price: 0,
  discount1_pct: 0,
  discount2_pct: 0,
  vat_applied: true,
  vat_pct: 15,
});

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const buildLevelOptions = (levels) =>
  (Array.isArray(levels) ? levels : [])
    .filter((l) => l.is_active !== false && (l.uom_id || l.uom?.id))
    .map((l) => ({
      value: l.uom_id || l.uom?.id,
      label: `${l.uom_code || l.uom?.code || ''} (× ${l.factor_to_base ?? 1})`,
      unit_price: l.unit_price,
    }));

const round2 = (v) => Math.round(v * 100) / 100;

const computeLine = (item) => {
  const gross = toNumber(item.quantity) * toNumber(item.unit_price);
  const d1 = round2(gross * (toNumber(item.discount1_pct) / 100));
  const d2 = round2((gross - d1) * (toNumber(item.discount2_pct) / 100));
  const discount = round2(d1 + d2);
  const net = round2(gross - discount);
  const vat = item.vat_applied ? round2(net * (toNumber(item.vat_pct) / 100)) : 0;
  const lineTotal = round2(net + vat);
  return { gross, discount, vat, lineTotal };
};

function RestoreSaleItemUOMs({ existing, onLevels }) {
  const { setFieldValue } = useFormikContext();
  const onLevelsRef = React.useRef(onLevels);
  useEffect(() => {
    onLevelsRef.current = onLevels;
  }, [onLevels]);

  useEffect(() => {
    if (!existing || !Array.isArray(existing.items)) return;
    let cancelled = false;
    (async () => {
      const erpClient = getERPClient();
      for (let idx = 0; idx < existing.items.length; idx++) {
        const line = existing.items[idx];
        const itemId = line.item_id || line.item?.id;
        if (!itemId) continue;
        try {
          const res = await erpClient.getItemPackagingLevels(itemId);
          const levels = Array.isArray(res) ? res : (res?.data || []);
          if (cancelled) return;
          onLevelsRef.current(itemId, buildLevelOptions(levels));
          const active = levels.filter((l) => l.is_active !== false);
          const savedUom = line.uom_id || line.uom?.id || line.entered_uom_id || '';
          const savedUomCode = line.uom || line.uom_name || '';
          const chosen =
            (savedUom && active.find((l) => String(l.uom_id || l.uom?.id) === String(savedUom))) ||
            (savedUomCode &&
              active.find((l) => {
                const code = String(l.uom_code || l.uom?.code || '').toLowerCase();
                return Object.prototype.hasOwnProperty.call(l, 'uom_code')
                  ? code === String(savedUomCode).toLowerCase()
                  : String(l.uom?.code || '').toLowerCase() === String(savedUomCode).toLowerCase();
              })) ||
            active.find((l) => l.is_selling_uom || l.is_default_sales) ||
            active.find((l) => l.is_base_uom) ||
            active[0] ||
            null;
          if (chosen?.uom_id) {
            setFieldValue(`items.${idx}.uom_id`, chosen.uom_id);
          } else if (savedUom) {
            setFieldValue(`items.${idx}.uom_id`, savedUom);
          }
        } catch (err) {
          console.error('Failed to restore item UOM:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing, setFieldValue]);

  return null;
}

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'MADA', label: 'Mada' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'MIXED', label: 'Mixed' },
];

export default function CreateERPSaleInvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [itemUomOptions, setItemUomOptions] = useState({});

  const itemsById = useMemo(() => items.reduce((acc, it) => { acc[it.id] = it; return acc; }, {}), [items]);

  useEffect(() => {
    async function loadMasterData() {
      try {
        const erpClient = getERPClient();
        const [custRes, itemRes, locRes] = await Promise.allSettled([
          erpClient.getCustomers({ limit: 500 }),
          erpClient.getItems({ limit: 500, is_active: true }),
          erpClient.getLocations(),
        ]);
        if (custRes.status === 'fulfilled' && custRes.value) {
          const list = Array.isArray(custRes.value) ? custRes.value : (custRes.value.data || []);
          setCustomers(list);
        }
        if (itemRes.status === 'fulfilled' && itemRes.value) {
          const list = Array.isArray(itemRes.value) ? itemRes.value : (itemRes.value.data || []);
          setItems(list);
        }
        if (locRes.status === 'fulfilled' && locRes.value) {
          const list = Array.isArray(locRes.value) ? locRes.value : (locRes.value.data || []);
          setLocations(list);
        }
      } catch (err) {
        console.error('Failed to load sale invoice master data:', err);
      }
    }
    loadMasterData();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const erpClient = getERPClient();
        const res = await erpClient.getSaleInvoice(id);
        const inv = res.data || res;
        if (inv && (inv.id || inv.invoice_number)) {
          setExisting(inv);
        }
      } catch (err) {
        console.error('Failed to load sale invoice:', err);
        toast.error(t('Failed to load sale invoice'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const customerOptions = (customers || []).map((c) => ({
    value: c.id,
    label: c.name || c.contact_name || c.id,
  }));

  const locationOptions = (locations || []).map((l) => {
    const id = l.id || l.location_id || l.uuid || '';
    const label = l.name_en || l.name_ar || l.name || l.code || id;
    return { value: id, label };
  });

  if (existing?.selling_location_id) {
    const selId = existing.selling_location_id || existing.location_id || existing.selling_location_snapshot?.id;
    if (selId && !locationOptions.some((o) => String(o.value) === String(selId))) {
      const snap = existing.selling_location_snapshot || {};
      locationOptions.push({
        value: selId,
        label: snap.name_en || snap.name_ar || snap.code || selId,
      });
    }
  }

  const customersById = useMemo(() => customers.reduce((acc, c) => { acc[c.id] = c; return acc; }, {}), [customers]);

  const setItemLevels = useCallback((itemId, options) => {
    setItemUomOptions((prev) => ({ ...prev, [itemId]: options }));
  }, []);

  const handleItemSelect = async (setFieldValue, index, selected) => {
    const item = typeof selected === 'string' ? itemsById[selected] : selected;
    const itemId = item?.id || item?.item_id || (typeof selected === 'string' ? selected : '');
    setFieldValue(`items.${index}.item_id`, itemId);
    setFieldValue(`items.${index}.item_code`, item?.item_code || '');
    setFieldValue(`items.${index}.item_name`, item?.name_en || item?.name_ar || item?.name || '');
    setFieldValue(`items.${index}.sku_id`, item?.sku_id || '');
    setFieldValue(`items.${index}.purchase_price`, toNumber(item?.standard_cost || item?.purchase_price || 0));
    const itemPrice = toNumber(item?.unit_price || item?.standard_cost || 0);
    if (!itemId) return;
    try {
      const erpClient = getERPClient();
      const res = await erpClient.getItemPackagingLevels(itemId);
      const levels = Array.isArray(res) ? res : (res?.data || []);
      const active = levels.filter((l) => l.is_active !== false);
      setItemLevels(itemId, buildLevelOptions(active));
      const chosen =
        active.find((l) => l.is_selling_uom || l.is_default_sales) ||
        active.find((l) => l.is_base_uom) ||
        active[0] ||
        null;
      if (chosen?.uom_id) {
        setFieldValue(`items.${index}.uom_id`, chosen.uom_id);
        const levelPrice = toNumber(chosen.unit_price);
        if (levelPrice > 0) {
          setFieldValue(`items.${index}.unit_price`, levelPrice);
        } else if (itemPrice > 0) {
          setFieldValue(`items.${index}.unit_price`, itemPrice);
        }
      }
    } catch (err) {
      console.error('Failed to load item packaging levels:', err);
      if (itemPrice > 0) setFieldValue(`items.${index}.unit_price`, itemPrice);
    }
  };

  const initialValues = useMemo(() => {
    const ex = existing || {};
    const rawItems = Array.isArray(ex.items) && ex.items.length > 0 ? ex.items : [];
    return {
      customer_id: ex.customer_id || ex.buyer_snapshot?.id || '',
      selling_location_id: ex.selling_location_id || ex.location_id || ex.selling_location_snapshot?.id || '',
      payment_method: ex.payment_method || 'CASH',
      payment_reference: ex.payment_reference || '',
      amount_paid: ex.amount_paid ?? ex.grand_total ?? '',
      discount: ex.header_discount ?? ex.invoice_discount ?? '',
      items: rawItems.length
        ? rawItems.map((line) => ({
          ...createItem(),
          ...line,
          item_id: line.item_id || line.item?.id || '',
          sku_id: line.sku_id || line.sku?.id || '',
          item_code: line.item_code || line.item?.item_code || '',
          item_name: line.item_name || line.item?.name_en || line.item?.name_ar || '',
          uom_id: line.uom_id || line.uom?.id || line.entered_uom_id || '',
          quantity: toNumber(line.quantity ?? 1),
          bonus_qty: toNumber(line.bonus_qty || 0),
          unit_price: toNumber(line.sale_price ?? line.unit_price ?? 0),
          purchase_price: toNumber(line.purchase_price || 0),
          discount1_pct: toNumber(line.discount1_pct || 0),
          discount2_pct: toNumber(line.discount2_pct || 0),
          vat_applied: typeof line.vat_applied === 'boolean' ? line.vat_applied : true,
          vat_pct: toNumber(line.vat_pct ?? 15),
        }))
        : [createItem()],
    };
  }, [existing]);

  const validationSchema = Yup.object({
    customer_id: Yup.string().required(t('Customer is required')),
    selling_location_id: Yup.string().required(t('Selling location is required')),
    payment_method: Yup.string().required(t('Payment method is required')),
    items: Yup.array()
      .of(
        Yup.object({
          item_id: Yup.string().required(t('Item is required')),
          quantity: Yup.number().moreThan(0, t('Quantity must be greater than 0')).required(t('Quantity is required')),
          unit_price: Yup.number().min(0, t('Sale price cannot be negative')).required(t('Sale price is required')),
        })
      )
      .min(1, t('At least one item required')),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const erpClient = getERPClient();
      const payload = {
        customer_id: values.customer_id,
        selling_location_id: values.selling_location_id,
        payment_method: values.payment_method,
        payment_reference: values.payment_reference || '',
        amount_paid: toNumber(values.amount_paid),
        discount: toNumber(values.discount),
        items: values.items.map((item) => ({
          item_id: item.item_id,
          sku_id: item.sku_id || null,
          location_id: values.selling_location_id,
          uom_id: item.uom_id || null,
          quantity: toNumber(item.quantity),
          bonus_qty: toNumber(item.bonus_qty),
          unit_price: toNumber(item.unit_price),
          purchase_price: toNumber(item.purchase_price),
          discount1_pct: toNumber(item.discount1_pct),
          discount2_pct: toNumber(item.discount2_pct),
          vat_applied: Boolean(item.vat_applied),
          vat_pct: toNumber(item.vat_pct),
        })),
      };

      if (isEdit) {
        await erpClient.updateSaleInvoice(id, payload);
        toast.success(t('Sale Invoice updated successfully'));
      } else {
        await erpClient.createSaleInvoice(payload);
        toast.success(t('Sale Invoice created successfully'));
      }
      navigate('/manager/erp/sale-invoices');
    } catch (error) {
      console.error('Failed to save sale invoice:', error);
      const msg = error.response?.data?.message || t('Error saving sale invoice');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="pi pi-spin pi-spinner text-3xl mb-2" />
        <p>{t('Loading Sale Invoice...')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-[1600px] mx-auto">
      <PageHeader
        title={isEdit ? t('Edit Sale Invoice') : t('Create Sale Invoice')}
        backPath="/manager/erp/sale-invoices"
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => {
          const computed = values.items.map(computeLine);
          const grossTotal = computed.reduce((s, l) => s + l.gross, 0);
          const itemDiscount = computed.reduce((s, l) => s + l.discount, 0);
          const vatTotal = computed.reduce((s, l) => s + l.vat, 0);
          const itemsTotal = computed.reduce((s, l) => s + l.lineTotal, 0);
          const headerDiscount = toNumber(values.discount);
          const grandTotal = itemsTotal - headerDiscount;

          return (
            <Form className="space-y-4">
              {isEdit && existing && (
                <RestoreSaleItemUOMs existing={existing} onLevels={setItemLevels} />
              )}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-5">
                  <SharedDropdown
                    label={`${t('Customer')} *`}
                    name="customer_id"
                    options={customerOptions}
                    onChange={(e) => {
                      const cust = customersById[e.value];
                      const defaults = {
                        discount1_pct: toNumber(cust?.default_discount_pct || 0),
                        discount2_pct: toNumber(cust?.default_discount2_pct || 0),
                        vat_applied: Boolean(cust?.vat_applicable),
                        vat_pct: toNumber(cust?.vat_rate || 0),
                      };
                      setFieldValue('customer_id', e.value);
                      values.items.forEach((_, idx) => {
                        Object.entries(defaults).forEach(([field, val]) =>
                          setFieldValue(`items.${idx}.${field}`, val)
                        );
                      });
                    }}
                  />
                  <SharedDropdown
                    label={`${t('Selling Location')} *`}
                    name="selling_location_id"
                    options={locationOptions}
                  />
                  <SharedDropdown label={`${t('Payment Method')} *`} name="payment_method" options={PAYMENT_METHODS} />
                  <SharedInputText label={t('Payment Reference')} name="payment_reference" placeholder={t('Optional reference...')} />
                </div>
                {(() => {
                  const cust = customersById[values.customer_id];
                  if (!cust) return null;
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
                      <span className="font-medium text-gray-700">{t('Customer defaults applied')}:</span>
                      <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
                        {t('Disc 1')}: {toNumber(cust.default_discount_pct || 0)}%
                      </span>
                      <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
                        {t('Disc 2')}: {toNumber(cust.default_discount2_pct || 0)}%
                      </span>
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                        {t('VAT')}:{' '}
                        {cust.vat_applicable === false
                          ? t('Exempt')
                          : `${toNumber(cust.vat_rate || 0)}%`}
                      </span>
                    </div>
                  );
                })()}
              </div>

              <FieldArray name="items" render={({ push, remove }) => (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-center border-b pb-2 mb-2">
                    <h3 className="font-semibold text-gray-800 text-sm">{t('Items')}</h3>
                    <SharedButton
                      type="button"
                      label={t('Add Another Item')}
                      buttonType="primary"
                      icon="pi pi-plus"
                      onClick={() => push(createItem())}
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600">
                          <th className="p-2 w-0" />
                          <th className="p-2 min-w-[170px] text-start">{t('Item')}</th>
                          <th className="p-2 min-w-[110px] text-start">{t('UOM')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('Qty')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('Bonus')}</th>
                          <th className="p-2 min-w-[95px] text-start">{t('Sale Price')}</th>
                          <th className="p-2 min-w-[65px] text-start">{t('Disc 1 %')}</th>
                          <th className="p-2 min-w-[65px] text-start">{t('Disc 2 %')}</th>
                          <th className="p-2 min-w-[50px] text-center">{t('VAT')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('VAT %')}</th>
                          <th className="p-2 min-w-[80px] text-end">{t('Total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.items.map((item, index) => {
                          const totals = computed[index] || { lineTotal: 0 };
                          const lineUomOptions = itemUomOptions[item.item_id];
                          const uomSelectOptions = lineUomOptions && lineUomOptions.length ? lineUomOptions : [];
                          return (
                            <tr key={index} className="border bg-gray-50">
                              <td className="p-2 align-top text-center">
                                <DeleteIcon
                                  onClick={() => remove(index)}
                                  className={values.items.length === 1 ? 'opacity-30 pointer-events-none' : ''}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <ERPItemSearchField
                                  name={item.item_id}
                                  items={items}
                                  isLoading={false}
                                  fallbackLabel={
                                    [item.item_code, item.item_name].filter(Boolean).join(' - ')
                                  }
                                  onSelect={(selected) =>
                                    handleItemSelect(setFieldValue, index, selected)
                                  }
                                />
                              </td>
                              <td className="p-2 align-top">
                                <select
                                  value={item.uom_id}
                                  onChange={(e) => {
                                    setFieldValue(`items.${index}.uom_id`, e.target.value);
                                    const opt = (lineUomOptions || []).find((o) => o.value === e.target.value);
                                    if (opt && opt.unit_price != null && toNumber(opt.unit_price) > 0) {
                                      setFieldValue(`items.${index}.unit_price`, toNumber(opt.unit_price));
                                    }
                                  }}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                >
                                  <option value="">{t('Select UOM')}</option>
                                  {uomSelectOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" value={item.quantity}
                                  onChange={(e) => setFieldValue(`items.${index}.quantity`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" value={item.bonus_qty}
                                  onChange={(e) => setFieldValue(`items.${index}.bonus_qty`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" step="any" value={item.unit_price}
                                  onChange={(e) => setFieldValue(`items.${index}.unit_price`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" max="100" value={item.discount1_pct}
                                  onChange={(e) => setFieldValue(`items.${index}.discount1_pct`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" max="100" value={item.discount2_pct}
                                  onChange={(e) => setFieldValue(`items.${index}.discount2_pct`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top text-center">
                                <input
                                  type="checkbox"
                                  checked={Boolean(item.vat_applied)}
                                  onChange={(e) => setFieldValue(`items.${index}.vat_applied`, e.target.checked)}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number" min="0" max="100" value={item.vat_pct}
                                  onChange={(e) => setFieldValue(`items.${index}.vat_pct`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top text-end whitespace-nowrap">
                                <span className="font-semibold text-primary">{totals.lineTotal.toFixed(2)}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )} />

              {errors && Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-md p-2">
                  {t('Please fix the highlighted fields before saving.')}
                </div>
              )}

              <div className="flex justify-end">
                <div className="w-full max-w-sm bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h3 className="text-xs font-semibold mb-2">{t('Summary')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('Subtotal')}</span>
                      <span>{grossTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('Item Discounts')}</span>
                      <span>{itemDiscount > 0 ? `-${itemDiscount.toFixed(2)}` : itemDiscount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('Tax')}</span>
                      <span>{vatTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2 mt-1">
                      <span>{t('Total')}</span>
                      <span className="text-primary">{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <SharedButton
                  type="button"
                  label={t('Cancel')}
                  icon="pi pi-times"
                  buttonType="reset"
                  onClick={() => navigate('/manager/erp/sale-invoices')}
                />
                <SharedButton
                  type="submit"
                  label={isSubmitting ? t('Saving...') : isEdit ? t('Update') : t('Submit Sale')}
                  icon="pi pi-check"
                  buttonType="primary"
                  loading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}